/**
 * HabitaScoreBadge — insignia circular con el Habita Score (1-100).
 *
 * Colores por band (paleta habita.co):
 *   - excellent (≥90): #0e7c62 (habita) + ring shadow
 *   - great (75-89): #10b981 (emerald)
 *   - good (60-74): #f59e0b (amber)
 *   - fair (40-59): #f97316 (orange)
 *   - low (<40): #ef4444 (rose)
 *
 * Se usa en tarjetas de propiedad. `size` controla el diámetro.
 */
import React from 'react';
import { Info } from 'lucide-react';

const BAND_COLORS = {
  excellent: { bg: '#0e7c62', ring: '#0e7c62' },
  great:     { bg: '#10b981', ring: '#10b981' },
  good:      { bg: '#f59e0b', ring: '#f59e0b' },
  fair:      { bg: '#f97316', ring: '#f97316' },
  low:       { bg: '#ef4444', ring: '#ef4444' },
};

const SIZE_MAP = {
  sm: { d: 40, font: 12, label: 8 },
  md: { d: 56, font: 16, label: 9 },
  lg: { d: 72, font: 22, label: 10 },
};

export default function HabitaScoreBadge({
  score,
  size = 'md',
  showLabel = true,
  className = '',
  testId,
}) {
  if (!score || typeof score.total !== 'number') return null;

  const band = score.band || 'good';
  const colors = BAND_COLORS[band] || BAND_COLORS.good;
  const dims = SIZE_MAP[size] || SIZE_MAP.md;

  return (
    <div
      className={`inline-flex flex-col items-center ${className}`}
      data-testid={testId || 'habita-score-badge'}
      title={score.headline || `Habita Score ${score.total}`}
    >
      <div
        className="rounded-full flex items-center justify-center font-serif font-bold text-white shadow-md"
        style={{
          width: dims.d,
          height: dims.d,
          background: `radial-gradient(circle at 30% 30%, ${colors.bg}, ${colors.bg}dd 60%, ${colors.bg}bb)`,
          border: `2px solid white`,
          boxShadow: `0 0 0 2px ${colors.ring}55, 0 4px 12px -4px ${colors.ring}66`,
          fontSize: dims.font,
          lineHeight: 1,
        }}
      >
        {score.total}
      </div>
      {showLabel && (
        <span
          className="mt-1 uppercase font-semibold tracking-widest text-habita-dark"
          style={{ fontSize: dims.label }}
        >
          Habita Score
        </span>
      )}
    </div>
  );
}

/** Versión compacta pill horizontal: útil dentro de un chip inline. */
export function HabitaScorePill({ score, className = '', testId }) {
  if (!score || typeof score.total !== 'number') return null;
  const band = score.band || 'good';
  const colors = BAND_COLORS[band] || BAND_COLORS.good;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-white text-[10px] font-bold shadow-sm ${className}`}
      style={{ background: colors.bg }}
      data-testid={testId || 'habita-score-pill'}
      title={score.headline || `Habita Score ${score.total}`}
    >
      <span className="font-serif text-[11px]">{score.total}</span>
      <span className="uppercase tracking-widest">Habita</span>
    </span>
  );
}

/** Explicación pequeña bajo el badge o al lado. */
export function HabitaScoreHeadline({ score, className = '' }) {
  if (!score?.headline) return null;
  return (
    <p className={`text-xs text-gray-600 italic leading-snug ${className}`}>
      <Info className="w-3 h-3 inline mr-1 text-habita" />
      Habita Score {score.total}: {score.headline}
    </p>
  );
}
